import { NavigationGuard } from "vue-router";
import { auth } from "@/utils/LtkAuth0";
import Authentication from ".";

const $authentication = new Authentication();
const wechatCodeItem = "wechatCode";
const setWechatCode = (code: string) => {
  localStorage.setItem(wechatCodeItem, code);
};
const getWechatCode = () => {
  return localStorage.getItem(wechatCodeItem) || '';
};
const clearWechatCode = () => {
  localStorage.removeItem(wechatCodeItem);
};

/**
 * Implicit On navigation guard requires the next route to specify
 * that it does not require auth before checking authentication status
 * of the user.
 */
const redirectRoute = async (config: any) => {
  const {
    isAuthenticated,
    isLambdaAuthorized,
    accessToken,
    idTokenClaims,
    to,
    next
  } = config;
  if (!isLambdaAuthorized) {
    await $authentication.lambdaAuthorize(
      accessToken,
      idTokenClaims,
      "login"
    );
  }
  if (!isAuthenticated) {
    return next({ path: "/login" });
  } else if (isAuthenticated && to.name === "Login") {
    return next({ path: "/logged-in" });
  } else {
    return next();
  }
};

const navigationGuard: NavigationGuard = async (to, from, next) => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code") || '';
  const openID = $authentication.openID;
  if (!openID && !getWechatCode()) {
    setWechatCode(code);
  }
  const fullPath = to.fullPath;

  if (!to.meta.requiresAuth && to.name !== "Login") {
    if (!to.meta.requiresAuth) {
      if (to.fullPath === "/" || !to.name) {
        return next({ path: "login" });
      }
      return next();
    }
    return next();
  }

  // when there's an openID in the local storage
  try {
    const accessToken = await auth.getAccessToken() || '';
    const idTokenClaims = (await auth.getIdTokenClaims())?.__raw;
    const isAuthenticated = await auth.isAuthenticated();
    if (openID) {
      clearWechatCode();
      const isLambdaAuthorized = await $authentication.checkLoggedIn(
        openID,
        accessToken
      );
      return redirectRoute({
        isAuthenticated,
        accessToken,
        idTokenClaims,
        isLambdaAuthorized,
        to,
        next
      });
    } else {
      // first time ever interact with the OA
      if (!getWechatCode()) return;
      const openID = await $authentication.getOpenID(
        getWechatCode(),
        accessToken
      );
      if (!openID) {
        if (isAuthenticated) {
          await auth.logout({
            logoutParams: {
              returnTo: `${window.location.origin}`
            }
          });
        }
        return;
      }
      $authentication.setOpenID(openID);
      const isLambdaAuthorized = await $authentication.checkLoggedIn(
        openID,
        accessToken
      );
      return redirectRoute({
        isAuthenticated,
        accessToken,
        idTokenClaims,
        isLambdaAuthorized,
        to,
        next
      });
    }
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.toLowerCase() === "login required"
    ) {
      await auth.login({
        options: {
          appState: {
            target: `${fullPath}`
          },
          openUrl: (url: string) => {
            window.location.replace(`${url}&lang=zh-Hans`);
          }
        },
        next: `${fullPath}`
      });
    }
  }
};

export default navigationGuard;
